import { cn, Select } from "@au-schein-de/au-ui-library";
import {
	type DeliveryMethodKind,
	VENDOR_AGGREGATION_GROUP,
} from "@dransay/api/models";
import { ScrollArea, Select as SelectPrimitive } from "radix-ui";
import {
	type ComponentRef,
	type FC,
	type ReactNode,
	type Ref,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import type {
	PharmacyPickerGroupShortcut,
	PharmacyPickerItem,
	PharmacyPickerNoResultsKind,
	PharmacyPickerNoResultsRecovery,
} from "~/features/products/pharmacy-picker/pharmacy-picker.types";
import {
	isMapDeliveryMethod,
	pharmacyPickerScrollBottomFadeClassName,
	pharmacyPickerScrollbarClassName,
	pharmacyPickerScrollbarThumbClassName,
} from "~/features/products/pharmacy-picker/pharmacy-picker.utils";
import { PharmacyPickerDeliveryMethodChip } from "~/features/products/pharmacy-picker/pharmacy-picker-delivery-method-chip.component";
import { PharmacyPickerFilterCards } from "~/features/products/pharmacy-picker/pharmacy-picker-filter-cards.component";
import { PharmacyPickerList } from "~/features/products/pharmacy-picker/pharmacy-picker-list.component";
import { PharmacyPickerMobileSearchOverlay } from "~/features/products/pharmacy-picker/pharmacy-picker-mobile-search-overlay.component";
import { PharmacyPickerMobileSheet } from "~/features/products/pharmacy-picker/pharmacy-picker-mobile-sheet.component";
import { isPharmacyRadiusPanelEvent } from "~/features/products/pharmacy-picker/pharmacy-picker-radius-panel.constants";
import {
	type PharmacyPickerSortContext,
	resolvePharmacyPickerSort,
	sortPharmacyPickerItems,
	usePharmacyPickerSort,
} from "~/features/products/pharmacy-picker/pharmacy-picker-sort-preference";
import { isPharmacyPickerSortPanelEvent } from "~/features/products/pharmacy-picker/pharmacy-picker-sort-side-panel.component";
import { PharmacyPickerStickyHeader } from "~/features/products/pharmacy-picker/pharmacy-picker-sticky-header.component";
import { PharmacyWarningBanner } from "~/features/products/pharmacy-picker/pharmacy-warning-banner.component";
import { useStuckObserver } from "~/features/products/pharmacy-picker/use-stuck-observer.hook";
import { useIsDesktop } from "~/hooks/use-is-desktop.hook";

const SEARCH_SECTION_HEIGHT_PX = 72;
const SEARCH_SECTION_WITH_FILTERS_HEIGHT_PX = 208;
/** Figma: 11px between the picker trigger and the dropdown panel. */
const PHARMACY_PICKER_CONTENT_SIDE_OFFSET_PX = 11;

function pharmacyPickerContentWidthClassName(
	enablePharmacyFilters: boolean,
): string {
	if (enablePharmacyFilters) {
		return "md:w-[458px]";
	}
	return "md:w-105";
}

function closePickerUnlessKeepOpen(
	keepOpenOnSelect: boolean,
	handleOpenChange: (open: boolean) => void,
): void {
	if (keepOpenOnSelect) {
		return;
	}
	handleOpenChange(false);
}

function afterMobileSearchGroupSelect(
	keepOpenOnSelect: boolean,
	setIsOpen: (open: boolean) => void,
	handleOpenChange: (open: boolean) => void,
): void {
	if (keepOpenOnSelect) {
		setIsOpen(true);
		return;
	}
	handleOpenChange(false);
}

function noResultsKindFromDelivery(
	kind: DeliveryMethodKind | undefined,
): PharmacyPickerNoResultsKind {
	if (kind === "pickup") return "pickup";
	if (kind === "courier" || kind === "express-courier") return "courier";
	return "shipping";
}

function buildNoResultsRecovery(
	enablePharmacyFilters: boolean,
	groupShortcuts: PharmacyPickerGroupShortcut[],
	deliveryMethodKind: DeliveryMethodKind | undefined,
	onSelectGroup: (groupId: string) => void,
): PharmacyPickerNoResultsRecovery | undefined {
	if (!enablePharmacyFilters) return undefined;
	const allGroup = groupShortcuts.find(
		(group) => group.id === VENDOR_AGGREGATION_GROUP.ALL,
	);
	if (!allGroup) return undefined;
	return {
		totalCount: allGroup.count,
		kind: noResultsKindFromDelivery(deliveryMethodKind),
		onDiscoverAll: () => onSelectGroup(VENDOR_AGGREGATION_GROUP.ALL),
	};
}

export type PharmacyPickerVendorSelectProps = {
	pharmacies: PharmacyPickerItem[];
	value?: string;
	onChange: (value: string) => void;
	onSearch: (query: string) => void;
	searchQuery: string;
	isLoading?: boolean;
	disabled?: boolean;
	showDistance?: boolean;
	sortContext?: PharmacyPickerSortContext;
	groupShortcuts: PharmacyPickerGroupShortcut[];
	onSelectGroup: (groupId: string, options?: { keepSearch?: boolean }) => void;
	onExpandNonTop?: () => void;
	activeGroupId?: string;
	/**
	 * Whether the list is narrowed to Top pharmacies. Separate from
	 * `activeGroupId`, which tracks the selection: a group row and a single
	 * pharmacy are one slot, so selecting a pharmacy clears the group without
	 * widening the list.
	 */
	topFilterSelected?: boolean;
	nonFeaturedCount?: number;
	/** Unscoped "of y" total for the available-count line, see PharmacyPickerList. */
	availableTotal?: number;
	onClose?: () => void;
	onDropdownOpenChange?: (isOpen: boolean) => void;
	triggerRef?: Ref<ComponentRef<typeof SelectPrimitive.Trigger>>;
	contentWrapper?: FC<{ children: ReactNode }>;
	mobileMapSlot?: ReactNode;
	footerSlot?: ReactNode;
	keepOpenOnSelect?: boolean;
	placeholder?: string;
	enablePharmacyFilters?: boolean;
	showSearch?: boolean;
	deliveryMethodKind?: DeliveryMethodKind;
	/** Figma 4249:80482: rendered inside the desktop filter row, see PharmacyPickerFilterCards. */
	radiusControl?: ReactNode;
	/** Mobile `variant="sheet"` counterpart, rendered inside the same filter row. */
	mobileRadiusControl?: ReactNode;
	/**
	 * Express-courier filter state. Owned by the surface that fetches the
	 * pharmacies, since the verdict comes from the list payload rather than
	 * from a row field the picker could filter on its own. Omitting
	 * `onExpressChange` leaves the chip out.
	 */
	expressSelected?: boolean;
	onExpressChange?: (selected: boolean) => void;
	/** Shown above the (empty) list when the express availability check failed. */
	expressCheckFailedMessage?: ReactNode;
};

export function PharmacyPickerVendorSelect({
	pharmacies,
	value,
	onChange,
	onSearch,
	searchQuery,
	isLoading = false,
	disabled,
	showDistance = false,
	sortContext = { surface: "list" },
	groupShortcuts,
	onSelectGroup,
	onExpandNonTop,
	activeGroupId,
	topFilterSelected = false,
	nonFeaturedCount = 0,
	availableTotal,
	onClose,
	onDropdownOpenChange,
	triggerRef,
	contentWrapper: ContentWrapper = ({ children }) => (
		<div className="h-[512px]">{children}</div>
	),
	mobileMapSlot,
	footerSlot,
	keepOpenOnSelect = false,
	placeholder,
	enablePharmacyFilters = false,
	showSearch = true,
	deliveryMethodKind,
	radiusControl,
	mobileRadiusControl,
	expressSelected,
	onExpressChange,
	expressCheckFailedMessage,
}: PharmacyPickerVendorSelectProps) {
	const { t } = useTranslation(["product", "common"]);
	const isDesktop = useIsDesktop();
	// Figma 4643:88826: with filters on, the mobile picker is a bottom sheet
	// instead of the trigger-anchored Select dropdown.
	const useMobileSheet = enablePharmacyFilters && !isDesktop;
	const [isOpen, setIsOpen] = useState(false);
	const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
	const [localSearch, setLocalSearch] = useState(searchQuery);
	const [payFilterSelected, setPayFilterSelected] = useState(false);
	const [sort] = usePharmacyPickerSort();
	const effectiveSort = resolvePharmacyPickerSort(sort, sortContext);
	// The Pay chip was selectable but purely decorative: it never narrowed the
	// list. `combinedPaymentEnabled` comes back on every row from
	// getVendorsFiltered, so the filter is client-side.
	const payFilteredPharmacies = useMemo(
		() =>
			payFilterSelected
				? pharmacies.filter((pharmacy) => pharmacy.combinedPaymentEnabled)
				: pharmacies,
		[pharmacies, payFilterSelected],
	);
	const visibleGroupShortcuts = useMemo(() => {
		if (!payFilterSelected) return groupShortcuts;
		const featuredCount = payFilteredPharmacies.filter(
			(pharmacy) => pharmacy.featured,
		).length;
		return groupShortcuts.map((group) => ({
			...group,
			count:
				group.id === VENDOR_AGGREGATION_GROUP.TOP
					? featuredCount
					: payFilteredPharmacies.length,
		}));
	}, [groupShortcuts, payFilteredPharmacies, payFilterSelected]);
	const visibleNonFeaturedCount = payFilterSelected
		? payFilteredPharmacies.filter((pharmacy) => !pharmacy.featured).length
		: nonFeaturedCount;
	const sortedPharmacies = useMemo(
		() => sortPharmacyPickerItems(payFilteredPharmacies, effectiveSort),
		[payFilteredPharmacies, effectiveSort],
	);
	useEffect(() => {
		setLocalSearch(searchQuery);
	}, [searchQuery]);
	const collisionBoundaryRef = useRef<HTMLDivElement>(null);
	const { sentinelRef, isStuck: isSearchElevated } = useStuckObserver(isOpen);
	const noSearchResults =
		searchQuery.trim().length > 0 && payFilteredPharmacies.length === 0;
	// Shipping is the only surface with a Top/non-Top split. Everywhere else the
	// list is simply every pharmacy, so the Top chip, the why-top banner and the
	// show-non-top recovery drop out with the Top group row itself.
	const offersTopGroup = visibleGroupShortcuts.some(
		(group) => group.id === VENDOR_AGGREGATION_GROUP.TOP,
	);
	const whyTopDescription =
		enablePharmacyFilters && !noSearchResults && offersTopGroup
			? t("vendor.picker.whyTopPharmacyDescription")
			: undefined;
	const noResultsRecovery = offersTopGroup
		? buildNoResultsRecovery(
				enablePharmacyFilters,
				visibleGroupShortcuts,
				deliveryMethodKind,
				onSelectGroup,
			)
		: undefined;
	const fillNoResults = enablePharmacyFilters && noSearchResults;
	const selectedPharmacy = pharmacies.find((p) => p.id === value);
	const triggerLabel = selectedPharmacy?.name ?? placeholder;
	const handleOpenChange = useCallback(
		(open: boolean) => {
			setIsOpen(open);
			onDropdownOpenChange?.(open);
			if (!open) onClose?.();
		},
		[onClose, onDropdownOpenChange],
	);
	const handleSelect = useCallback(
		(pharmacyId: string) => {
			onChange(pharmacyId);
			setIsMobileSearchOpen(false);
			if (keepOpenOnSelect) {
				// Selecting from the fullscreen mobile search must land back in the
				// open picker, where the confirm CTA lives (no-op when the picker
				// is already open).
				setIsOpen(true);
				return;
			}
			handleOpenChange(false);
		},
		[onChange, keepOpenOnSelect, handleOpenChange],
	);
	const openMobileSearch = useCallback(() => {
		// Close the dropdown (without the onClose reset that handleOpenChange runs)
		// and open the full-screen search instead.
		setIsOpen(false);
		setIsMobileSearchOpen(true);
	}, []);
	const closeMobileSearch = useCallback(() => {
		setIsMobileSearchOpen(false);
		setIsOpen(true);
	}, []);
	// Figma 4643:88797: chip toggles keep the typed search — filters narrow the
	// current matches instead of resetting the input.
	const handleTopChange = offersTopGroup
		? (selected: boolean) => {
				if (selected) {
					onSelectGroup(VENDOR_AGGREGATION_GROUP.TOP, { keepSearch: true });
					return;
				}
				onExpandNonTop?.();
				onSelectGroup(VENDOR_AGGREGATION_GROUP.ALL, { keepSearch: true });
			}
		: undefined;
	const listContent = useMemo(
		() => (
			<PharmacyPickerList
				pharmacies={sortedPharmacies}
				selectedId={value}
				onSelect={handleSelect}
				showDistance={showDistance}
				searchQuery={searchQuery}
				groupShortcuts={visibleGroupShortcuts}
				onSelectGroup={(groupId) => {
					onSelectGroup(groupId);
					closePickerUnlessKeepOpen(keepOpenOnSelect, handleOpenChange);
				}}
				onSwitchGroup={onSelectGroup}
				onExpandNonTop={offersTopGroup ? onExpandNonTop : undefined}
				activeGroupId={activeGroupId}
				nonFeaturedCount={offersTopGroup ? visibleNonFeaturedCount : 0}
				availableTotal={availableTotal}
				bannerBreakoutClassName="-mx-4 px-4"
				className="flex flex-col gap-3 px-4 pt-3"
				noResultsMessageInHeader={noSearchResults && !noResultsRecovery}
				isMapDelivery={!!mobileMapSlot}
				hideWhyTopCollapsible={enablePharmacyFilters}
				showTopSplit={offersTopGroup}
				noResultsRecovery={noResultsRecovery}
			/>
		),
		[
			sortedPharmacies,
			value,
			handleSelect,
			showDistance,
			searchQuery,
			visibleGroupShortcuts,
			onSelectGroup,
			offersTopGroup,
			onExpandNonTop,
			activeGroupId,
			visibleNonFeaturedCount,
			availableTotal,
			handleOpenChange,
			keepOpenOnSelect,
			noSearchResults,
			mobileMapSlot,
			enablePharmacyFilters,
			noResultsRecovery,
		],
	);
	const stickyHeader = (
		<PharmacyPickerStickyHeader
			isElevated={isSearchElevated}
			showSearch={showSearch}
			showFilterCards={enablePharmacyFilters}
			sortContext={sortContext}
			searchValue={localSearch}
			searchPlaceholder={t("search.vendor.placeholder.withLocalSearch")}
			clearSearchLabel={t("common:header.addressModal.clearSearch")}
			onSearchChange={(next) => {
				setLocalSearch(next);
				onSearch(next);
			}}
			onSearchClear={() => {
				setLocalSearch("");
				onSearch("");
			}}
			onMobileSearchOpen={openMobileSearch}
			noSearchResults={noSearchResults && !noResultsRecovery}
			topSelected={topFilterSelected}
			paySelected={payFilterSelected}
			onTopChange={handleTopChange}
			onPayChange={setPayFilterSelected}
			description={whyTopDescription}
			radiusControl={radiusControl}
			mobileRadiusControl={mobileRadiusControl}
			deliveryMethodControl={<PharmacyPickerDeliveryMethodChip />}
			expressSelected={expressSelected}
			onExpressChange={onExpressChange}
			expressCheckFailedMessage={expressCheckFailedMessage}
		/>
	);
	const loadingIndicator = isLoading ? (
		<div className="p-4 text-center text-gray-500">
			{t("common:loading", { defaultValue: "Loading..." })}
		</div>
	) : null;
	return (
		<>
			<Select.Root
				variant="pharmacy"
				value={value}
				onValueChange={(next) => onChange(next)}
				open={isOpen && !useMobileSheet}
				onOpenChange={handleOpenChange}
				disabled={disabled}
			>
				<div
					ref={collisionBoundaryRef}
					className="absolute top-120 bottom-0 left-0 right-0 md:-left-3 md:-right-3 z-(--z-index-hidden)"
				/>
				<Select.Trigger
					className="w-full text-body-bold"
					data-testid="vendor-filter"
					ref={triggerRef}
				>
					<Select.Value placeholder={placeholder}>{triggerLabel}</Select.Value>
				</Select.Trigger>
				<SelectPrimitive.Portal>
					<SelectPrimitive.Content
						position="popper"
						side="bottom"
						collisionBoundary={collisionBoundaryRef.current}
						sideOffset={PHARMACY_PICKER_CONTENT_SIDE_OFFSET_PX}
						data-testid="pharmacy-picker-content"
						onPointerDownOutside={(event) => {
							if (
								isPharmacyPickerSortPanelEvent(event) ||
								isPharmacyRadiusPanelEvent(event)
							) {
								event.preventDefault();
							}
						}}
						className={cn(
							"relative flex w-screen -ml-1.5 md:ml-0 max-w-full w-radix-select flex-col text-popover-foreground max-md:rounded-none max-md:shadow-none",
							enablePharmacyFilters
								? "overflow-hidden md:overflow-visible"
								: "overflow-hidden md:rounded-2xl md:shadow-[0px_10px_20px_0px_rgba(33,42,54,0.16)]",
							pharmacyPickerContentWidthClassName(enablePharmacyFilters),
							fillNoResults ? "bg-gray-100 md:h-[616px]" : "bg-white",
							"data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
						)}
					>
						<div
							className={cn(
								"flex min-h-0 flex-1 flex-col overflow-hidden",
								fillNoResults ? "bg-gray-100 md:h-[616px]" : "bg-white",
								enablePharmacyFilters &&
									cn(
										"max-md:rounded-none md:shadow-[0px_10px_20px_0px_rgba(33,42,54,0.16)]",
										isMapDeliveryMethod(deliveryMethodKind)
											? "md:rounded-l-2xl md:rounded-r-none"
											: "md:rounded-2xl",
									),
							)}
							data-testid="pharmacy-picker-panel"
						>
							{stickyHeader}
							<ScrollArea.Root
								className={cn(
									"relative min-h-0 flex-1",
									// Matches the panel's own target height so this cap
									// never binds before the computed list height does.
									fillNoResults ? "bg-gray-100" : "md:max-h-[616px]",
								)}
								type="auto"
							>
								<ContentWrapper>
									<ScrollArea.Viewport
										className={cn(
											"relative h-full bg-gray-100 pb-4 [&>div]:!block",
											fillNoResults && "[&>div]:h-full",
										)}
									>
										<div
											ref={sentinelRef}
											className="pointer-events-none h-px w-full shrink-0"
											aria-hidden="true"
										/>
										{mobileMapSlot && !noSearchResults && (
											<div className="px-4 py-2 md:hidden">{mobileMapSlot}</div>
										)}
										{loadingIndicator}
										{!isLoading && listContent}
									</ScrollArea.Viewport>
									<ScrollArea.Scrollbar
										className={pharmacyPickerScrollbarClassName}
										orientation="vertical"
									>
										<ScrollArea.Thumb
											className={pharmacyPickerScrollbarThumbClassName}
										/>
									</ScrollArea.Scrollbar>
								</ContentWrapper>
								{/* Anchored to the scroll area's visual bottom (not the scrolled
						    content) so the fade stays at the bottom even when a short
						    result list does not fill the fixed-height viewport. Hidden
						    for the compact no-results layout, which never scrolls. */}
								{!noSearchResults && (
									<div
										aria-hidden="true"
										className={pharmacyPickerScrollBottomFadeClassName}
										data-testid="pharmacy-picker-scroll-bottom-fade"
									/>
								)}
							</ScrollArea.Root>
							{footerSlot && !fillNoResults ? (
								<div className="relative shrink-0 bg-white px-4 py-4 shadow-[0_4px_20px_0_rgba(33,42,54,0.1)]">
									{footerSlot}
								</div>
							) : null}
						</div>
						{enablePharmacyFilters ? (
							<div
								className="pointer-events-none absolute inset-0 z-[52] overflow-visible"
								data-testid="pharmacy-picker-panel-slot"
							/>
						) : null}
					</SelectPrimitive.Content>
				</SelectPrimitive.Portal>
			</Select.Root>
			{useMobileSheet ? (
				<PharmacyPickerMobileSheet
					open={isOpen}
					onOpenChange={handleOpenChange}
					title={
						isMapDeliveryMethod(deliveryMethodKind)
							? t("vendor.picker.sheet.titleNearby")
							: t("vendor.pdp.vendorSelectionCTA")
					}
					closeLabel={t("common:close")}
				>
					{stickyHeader}
					<div className="relative flex min-h-0 flex-1 flex-col">
						<div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain bg-gray-100 pb-4">
							<div
								ref={sentinelRef}
								className="pointer-events-none h-px w-full shrink-0"
								aria-hidden="true"
							/>
							{mobileMapSlot && !noSearchResults && (
								<div className="px-4 py-2">{mobileMapSlot}</div>
							)}
							{loadingIndicator}
							{!isLoading && listContent}
						</div>
						{!noSearchResults && (
							<div
								aria-hidden="true"
								className={pharmacyPickerScrollBottomFadeClassName}
								data-testid="pharmacy-picker-scroll-bottom-fade"
							/>
						)}
					</div>
					{footerSlot ? (
						<div className="relative shrink-0 bg-white px-4 py-4 shadow-[0_4px_20px_0_rgba(33,42,54,0.1)]">
							{footerSlot}
						</div>
					) : null}
				</PharmacyPickerMobileSheet>
			) : null}
			<PharmacyPickerMobileSearchOverlay
				open={isMobileSearchOpen}
				onClose={closeMobileSearch}
				value={localSearch}
				onValueChange={(next) => {
					setLocalSearch(next);
					onSearch(next);
				}}
				placeholder={t("search.vendor.placeholder.withLocalSearch")}
				toolbar={
					enablePharmacyFilters ? (
						<div className="flex flex-col gap-3">
							<PharmacyPickerFilterCards
								className="-mx-3 px-3"
								topSelected={topFilterSelected}
								paySelected={payFilterSelected}
								onTopChange={handleTopChange}
								onPayChange={setPayFilterSelected}
								sortContext={sortContext}
								mobileRadiusControl={mobileRadiusControl}
								deliveryMethodControl={<PharmacyPickerDeliveryMethodChip />}
								expressSelected={expressSelected}
								onExpressChange={onExpressChange}
							/>
							{whyTopDescription && !topFilterSelected ? (
								<PharmacyWarningBanner data-testid="pharmacy-picker-why-top-blurb">
									{whyTopDescription}
								</PharmacyWarningBanner>
							) : null}
						</div>
					) : null
				}
			>
				<PharmacyPickerList
					pharmacies={sortedPharmacies}
					selectedId={value}
					onSelect={handleSelect}
					showDistance={showDistance}
					searchQuery={searchQuery}
					groupShortcuts={visibleGroupShortcuts}
					onSelectGroup={(groupId) => {
						onSelectGroup(groupId);
						setIsMobileSearchOpen(false);
						afterMobileSearchGroupSelect(
							keepOpenOnSelect,
							setIsOpen,
							handleOpenChange,
						);
					}}
					onSwitchGroup={onSelectGroup}
					onExpandNonTop={onExpandNonTop}
					activeGroupId={activeGroupId}
					nonFeaturedCount={visibleNonFeaturedCount}
					availableTotal={availableTotal}
					bannerBreakoutClassName="-mx-4 px-4"
					className="flex flex-col gap-3 px-4 pb-4 pt-3"
					noResultsMessageInHeader={noSearchResults && !noResultsRecovery}
					isMapDelivery={!!mobileMapSlot}
					hideWhyTopCollapsible={enablePharmacyFilters}
					noResultsRecovery={noResultsRecovery}
				/>
			</PharmacyPickerMobileSearchOverlay>
		</>
	);
}

PharmacyPickerVendorSelect.SEARCH_SECTION_HEIGHT_PX = SEARCH_SECTION_HEIGHT_PX;
PharmacyPickerVendorSelect.SEARCH_SECTION_WITH_FILTERS_HEIGHT_PX =
	SEARCH_SECTION_WITH_FILTERS_HEIGHT_PX;
PharmacyPickerVendorSelect.CONTENT_SIDE_OFFSET_PX =
	PHARMACY_PICKER_CONTENT_SIDE_OFFSET_PX;
